import React from 'react'
import {
  TrendingUp,
  TrendingDown,
  ArrowDownLeft,
  ArrowUpRight,
  Repeat,
  BookmarkCheck,
  DollarSign,
  BarChart3,
  Layers,
} from 'lucide-react'
import { formatVnNumber } from '@/features/purchasing/poWorkflow'
import {
  MATERIAL_CATEGORY_LABELS,
  type ManagementDashboardData,
  type MaterialCategory,
} from '@/services/reports/inventoryDashboardApi'

export interface ManagementDashboardTabProps {
  data: ManagementDashboardData
  loading?: boolean
  selectedCategory?: MaterialCategory
}

export function ManagementDashboardTab({
  data,
  loading: _loading,
  selectedCategory,
}: ManagementDashboardTabProps): React.ReactElement {
  const isFiltered = Boolean(selectedCategory && selectedCategory !== 'ALL')
  const rawGroups = data.groupStockStatus || []
  // total count across all groups

  // 1. Hide standard groups with 0 materials when not filtering for a specific group
  // If a specific group is selected, show that group even if it has 0 items ("0 mã vật tư")
  // 2. Ensure "Chưa phân nhóm" ('other') always appears at the end
  const visibleGroups = rawGroups
    .filter((g) => (isFiltered ? true : g.total > 0))
    .sort((a, b) => {
      if (a.group_code === 'other') return 1
      if (b.group_code === 'other') return -1
      return 0
    })

  const unassignedCount = rawGroups.find((g) => g.group_code === 'other')?.total || 0
  const isNetPositive = data.netChangeQty >= 0

  // Chart calculations for SVG bar chart
  const trend = data.monthlyTrend
  // trend data

  return (
    <div className="space-y-6">
      {/* Period Movement Metric Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-4">
        {/* Nhập kho */}
        <div className="rounded-[var(--radius-lg)] border border-emerald-200 bg-white p-3.5 shadow-[var(--shadow-level-1)]">
          <div className="flex items-center justify-between text-xs font-semibold text-emerald-700 uppercase">
            <span>Nhập kho trong kỳ</span>
            <ArrowDownLeft className="h-4 w-4" />
          </div>
          <div className="mt-2 text-xl sm:text-2xl font-bold tabular-nums text-emerald-700 break-normal">
            {formatVnNumber(data.inboundQty)}
          </div>
          <div className="mt-1 text-xs text-[var(--color-text-muted)]">
            {formatVnNumber(data.inboundVoucherCount)} phiếu nhập phát sinh
          </div>
        </div>

        {/* Xuất kho */}
        <div className="rounded-[var(--radius-lg)] border border-blue-200 bg-white p-3.5 shadow-[var(--shadow-level-1)]">
          <div className="flex items-center justify-between text-xs font-semibold text-blue-700 uppercase">
            <span>Xuất kho trong kỳ</span>
            <ArrowUpRight className="h-4 w-4" />
          </div>
          <div className="mt-2 text-xl sm:text-2xl font-bold tabular-nums text-blue-700 break-normal">
            {formatVnNumber(data.outboundQty)}
          </div>
          <div className="mt-1 text-xs text-[var(--color-text-muted)]">
            {formatVnNumber(data.outboundVoucherCount)} phiếu xuất phát sinh
          </div>
        </div>

        {/* Biến động ròng */}
        <div
          className={`rounded-[var(--radius-lg)] border p-3.5 shadow-[var(--shadow-level-1)] ${
            isNetPositive
              ? 'border-emerald-300 bg-emerald-50/40'
              : 'border-amber-300 bg-amber-50/40'
          }`}
        >
          <div className="flex items-center justify-between text-xs font-semibold uppercase">
            <span className={isNetPositive ? 'text-emerald-800' : 'text-amber-800'}>
              Biến động ròng (Net)
            </span>
            {isNetPositive ? (
              <TrendingUp className="h-4 w-4 text-emerald-700" />
            ) : (
              <TrendingDown className="h-4 w-4 text-amber-700" />
            )}
          </div>
          <div
            className={`mt-2 text-xl sm:text-2xl font-bold tabular-nums break-normal ${
              isNetPositive ? 'text-emerald-700' : 'text-amber-700'
            }`}
          >
            {isNetPositive ? `+${formatVnNumber(data.netChangeQty)}` : formatVnNumber(data.netChangeQty)}
          </div>
          <div className="mt-1 text-xs text-[var(--color-text-muted)]">
            Chênh lệch = Nhập - Xuất
          </div>
        </div>

        {/* Tồn đầu kỳ & Cuối kỳ */}
        <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white p-3.5 shadow-[var(--shadow-level-1)]">
          <div className="flex items-center justify-between text-xs font-semibold text-[var(--color-text-muted)] uppercase">
            <span>Tồn đầu & Cuối kỳ</span>
            <Layers className="h-4 w-4 text-slate-500" />
          </div>
          <div className="mt-2 flex flex-col sm:flex-row sm:items-baseline justify-between text-sm sm:text-base font-semibold tabular-nums text-slate-800 gap-0.5 sm:gap-2">
            <span>Đầu: {formatVnNumber(data.openingStock)}</span>
            <span className="text-sm font-bold text-slate-900">
              Cuối: {formatVnNumber(data.closingStock)}
            </span>
          </div>
          <div className="mt-1 text-xs text-[var(--color-text-muted)]">
            Tổng sản lượng tồn kho
          </div>
        </div>

        {/* Giá trị tồn kho */}
        <div className="col-span-2 sm:col-span-1 lg:col-span-1 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white p-3.5 shadow-[var(--shadow-level-1)]">
          <div className="flex items-center justify-between text-xs font-semibold text-[var(--color-text-muted)] uppercase">
            <span>Giá trị tồn kho (VNĐ)</span>
            <DollarSign className="h-4 w-4 text-slate-500" />
          </div>
          <div className="mt-2 text-lg sm:text-xl font-bold tabular-nums text-slate-900 break-normal">
            {formatVnNumber(data.totalValuationVnd)} ₫
          </div>
          <div className="mt-1 text-xs text-[var(--color-text-muted)]">
            {data.unpricedQty > 0 ? (
              <span className="text-amber-600 font-medium">
                {data.unpricedQty} vật tư chưa xác định giá
              </span>
            ) : (
              '100% vật tư đã có giá vốn'
            )}
          </div>
        </div>

        {/* Thống kê Chuyển mã tự do */}
        <div className="col-span-2 sm:col-span-1 lg:col-span-1 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white p-3.5 shadow-[var(--shadow-level-1)]">
          <div className="flex items-center justify-between text-xs font-semibold text-[var(--color-text-muted)] uppercase">
            <span>Chuyển mã CHT</span>
            <Repeat className="h-4 w-4 text-indigo-500" />
          </div>
          <div className="mt-2 text-lg sm:text-xl font-bold tabular-nums text-indigo-700 break-normal">
            {formatVnNumber(data.conversionCount)} lượt chuyển
          </div>
          <div className="mt-1 text-xs text-[var(--color-text-muted)]">
            Tự do: {formatVnNumber(data.conversionSourceQty)} → Chuẩn: {formatVnNumber(data.conversionTargetQty)}
          </div>
        </div>

        {/* Thống kê Giữ hàng */}
        <div className="col-span-2 sm:col-span-2 lg:col-span-2 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white p-3.5 shadow-[var(--shadow-level-1)]">
          <div className="flex items-center justify-between text-xs font-semibold text-[var(--color-text-muted)] uppercase">
            <span>Phát sinh giữ hàng trong kỳ</span>
            <BookmarkCheck className="h-4 w-4 text-amber-600" />
          </div>
          <div className="mt-2 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
            <span className="text-lg sm:text-xl font-bold tabular-nums text-amber-700 whitespace-nowrap">
              {formatVnNumber(data.activeHoldCount)} phiếu giữ phát sinh
            </span>
            <span className="text-xs text-[var(--color-text-muted)]">
              Phục vụ nhu cầu cấp phát các Lệnh sản xuất (LSX)
            </span>
          </div>
          <div className="mt-1 text-xs text-[var(--color-text-muted)]">
            Đảm bảo vật tư không bị xuất trùng lặp cho các đơn hàng khác
          </div>
        </div>
      </div>

      {/* Tình trạng tồn kho theo nhóm vật tư (Horizontal Stacked Bar Chart) */}
      <div data-testid="group-stock-card" className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white p-4 shadow-[var(--shadow-level-1)]">
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-700">
              <BarChart3 className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                Tình trạng tồn kho theo nhóm vật tư
              </h3>
              <p className="text-xs text-[var(--color-text-muted)]">
                Phân bổ số lượng mã vật tư theo tình trạng tồn kho trên từng nhóm vật tư chính
              </p>
            </div>
          </div>
          {/* Legend */}
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <div className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-xs bg-rose-500 inline-block" />
              <span className="text-slate-700 font-medium">Hết hàng</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-xs bg-amber-500 inline-block" />
              <span className="text-slate-700 font-medium">Dưới định mức min</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-xs bg-slate-400 inline-block" />
              <span className="text-slate-700 font-medium">Tồn lâu (&gt; 30 ngày)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-xs bg-emerald-500 inline-block" />
              <span className="text-slate-700 font-medium">Bình thường</span>
            </div>
          </div>
        </div>

        {/* Horizontal Stacked Bars */}
        <div className="space-y-3.5 pt-2 pb-1" data-testid="group-stock-chart">
          {visibleGroups.length === 0 ? (
            <div className="py-6 text-center text-xs text-[var(--color-text-muted)] italic">
              Chưa có vật tư nào trong kho
            </div>
          ) : (
            visibleGroups.map((g) => {
              const groupName =
                MATERIAL_CATEGORY_LABELS[g.group_code] ||
                g.group_name ||
                g.group_code
              const total = g.total
              const outPct = total > 0 ? (g.out_of_stock / total) * 100 : 0
              const belowPct = total > 0 ? (g.below_min / total) * 100 : 0
              const slowPct = total > 0 ? (g.slow_moving / total) * 100 : 0
              const normPct = total > 0 ? (g.normal / total) * 100 : 0

              return (
                <div key={g.group_code} className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs font-semibold text-slate-800">
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold">{groupName}</span>
                      {g.group_code !== 'other' && (
                        <span className="font-mono text-[11px] text-[var(--color-text-muted)] font-normal">
                          {`(${g.group_code})`}
                        </span>
                      )}
                    </div>
                    <span className="tabular-nums text-[11px] text-[var(--color-text-muted)] font-medium">
                      {`${formatVnNumber(total)} mã vật tư`}
                    </span>
                  </div>

                  {/* Horizontal Stacked Bar */}
                  <div className="relative flex h-7 w-full overflow-hidden rounded bg-slate-100 shadow-inner">
                    {total === 0 ? (
                      <div className="flex h-full w-full items-center justify-center text-[11px] text-slate-400 italic">
                        0 mã vật tư
                      </div>
                    ) : (
                      <>
                        {/* 1. Hết hàng */}
                        {g.out_of_stock > 0 && (
                          <div
                            style={{ width: `${outPct}%` }}
                            className="flex h-full items-center justify-center bg-rose-500 text-white font-semibold text-[11px] tabular-nums transition-all hover:brightness-110 cursor-default"
                            title={`${groupName} — Hết hàng: ${formatVnNumber(g.out_of_stock)} mã (${Math.round(outPct)}%)`}
                          >
                            {outPct >= 8 ? formatVnNumber(g.out_of_stock) : ''}
                          </div>
                        )}

                        {/* 2. Dưới định mức min */}
                        {g.below_min > 0 && (
                          <div
                            style={{ width: `${belowPct}%` }}
                            className="flex h-full items-center justify-center bg-amber-500 text-white font-semibold text-[11px] tabular-nums transition-all hover:brightness-110 cursor-default"
                            title={`${groupName} — Dưới định mức min: ${formatVnNumber(g.below_min)} mã (${Math.round(belowPct)}%)`}
                          >
                            {belowPct >= 8 ? formatVnNumber(g.below_min) : ''}
                          </div>
                        )}

                        {/* 3. Tồn lâu > 30 ngày */}
                        {g.slow_moving > 0 && (
                          <div
                            style={{ width: `${slowPct}%` }}
                            className="flex h-full items-center justify-center bg-slate-400 text-white font-semibold text-[11px] tabular-nums transition-all hover:brightness-110 cursor-default"
                            title={`${groupName} — Tồn lâu > 30 ngày: ${formatVnNumber(g.slow_moving)} mã (${Math.round(slowPct)}%)`}
                          >
                            {slowPct >= 8 ? formatVnNumber(g.slow_moving) : ''}
                          </div>
                        )}

                        {/* 4. Bình thường */}
                        {g.normal > 0 && (
                          <div
                            style={{ width: `${normPct}%` }}
                            className="flex h-full items-center justify-center bg-emerald-500 text-white font-semibold text-[11px] tabular-nums transition-all hover:brightness-110 cursor-default"
                            title={`${groupName} — Bình thường: ${formatVnNumber(g.normal)} mã (${Math.round(normPct)}%)`}
                          >
                            {normPct >= 8 ? formatVnNumber(g.normal) : ''}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>

        {unassignedCount > 0 && (
          <p
            data-testid="unassigned-hint"
            className="mt-2 text-xs text-amber-700 font-medium"
          >
            {`${formatVnNumber(unassignedCount)} vật tư chưa được gán nhóm — gán nhóm ở Danh mục vật tư để biểu đồ chính xác hơn.`}
          </p>
        )}

        <p className="mt-3 text-[11px] text-[var(--color-text-muted)] italic">
          * Thứ tự ưu tiên phân loại: Hết hàng &gt; Dưới định mức &gt; Tồn lâu &gt; Bình thường (mỗi mã vật tư chỉ tính vào một tình trạng duy nhất).
        </p>

        {/* Compact Table: Nhập - Xuất 6 tháng gần nhất */}
        {trend.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-800">
                Nhập – Xuất 6 tháng gần nhất
              </span>
              <span className="text-[11px] text-[var(--color-text-muted)]">
                Dữ liệu phát sinh thực tế theo chứng từ
              </span>
            </div>
            <div className="overflow-x-auto rounded border border-[var(--color-border)]">
              <table className="min-w-full divide-y divide-[var(--color-border)] text-left text-xs">
                <thead className="bg-slate-50 font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-center w-10">
                      STT
                    </th>
                    <th scope="col" className="px-3 py-2">
                      Tháng
                    </th>
                    <th scope="col" className="px-3 py-2 text-right">
                      Nhập kho
                    </th>
                    <th scope="col" className="px-3 py-2 text-right">
                      Xuất kho
                    </th>
                    <th scope="col" className="px-3 py-2 text-right">
                      Biến động ròng (Net)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)] bg-white">
                  {trend.map((pt, idx) => (
                    <tr key={pt.month || idx} className="hover:bg-slate-50/80">
                      <td className="px-3 py-1.5 text-center text-[var(--color-text-muted)]">
                        {idx + 1}
                      </td>
                      <td className="px-3 py-1.5 font-semibold text-slate-800">
                        {pt.month}
                      </td>
                      <td className="px-3 py-1.5 text-right font-medium tabular-nums text-emerald-700">
                        {formatVnNumber(pt.inbound)}
                      </td>
                      <td className="px-3 py-1.5 text-right font-medium tabular-nums text-blue-700">
                        {formatVnNumber(pt.outbound)}
                      </td>
                      <td
                        className={`px-3 py-1.5 text-right font-bold tabular-nums ${
                          pt.net >= 0 ? 'text-emerald-700' : 'text-amber-700'
                        }`}
                      >
                        {pt.net >= 0 ? `+${formatVnNumber(pt.net)}` : formatVnNumber(pt.net)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* 2 Top 10 Tables Grid */}
      <div data-testid="top10-tables-container" className="grid grid-cols-1 gap-4 2xl:gap-3 2xl:grid-cols-2">
        {/* Table 1: Top 10 Outbound Items */}
        <div className="flex flex-col min-w-0 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white p-3 sm:p-4 shadow-[var(--shadow-level-1)]">
          <div className="mb-3 min-h-[50px] flex flex-col justify-start">
            <h3 className="text-base font-semibold text-slate-900 leading-snug">
              Top 10 vật tư xuất nhiều nhất trong kỳ
            </h3>
            <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
              Xếp hạng theo tổng sản lượng vật tư xuất phục vụ sản xuất
            </p>
          </div>

          <div className="overflow-x-auto rounded border border-[var(--color-border)]">
            <table className="w-full min-w-[520px] 2xl:min-w-0 2xl:table-fixed divide-y divide-[var(--color-border)] text-left text-xs sm:text-sm">
              <thead className="bg-slate-50 text-[11px] sm:text-xs font-semibold uppercase tracking-tight text-[var(--color-text-muted)]">
                <tr>
                  <th scope="col" className="px-2 py-2 text-center w-8 whitespace-nowrap">
                    STT
                  </th>
                  <th scope="col" className="px-2 py-2 whitespace-nowrap w-24">
                    Mã VT
                  </th>
                  <th scope="col" className="px-2 py-2 min-w-[180px] sm:min-w-[220px]">
                    Tên vật tư
                  </th>
                  <th scope="col" className="px-2 py-2 text-center w-11 whitespace-nowrap">
                    ĐVT
                  </th>
                  <th scope="col" className="px-2 py-2 text-right w-20">
                    Tổng xuất
                  </th>
                  <th scope="col" className="px-2 py-2 text-right w-22">
                    Số lần xuất
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)] bg-white">
                {data.topOutboundItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="sticky left-0 px-3 py-6 text-center text-xs text-[var(--color-text-muted)] italic">
                      Chưa có phát sinh xuất kho trong kỳ
                    </td>
                  </tr>
                ) : (
                  data.topOutboundItems.map((item, idx) => (
                    <tr key={item.id} className="hover:bg-slate-50/80">
                      <td className="px-2 py-2 text-center text-xs text-[var(--color-text-muted)] whitespace-nowrap">
                        {idx + 1}
                      </td>
                      <td className="px-2 py-2 font-mono text-xs font-semibold text-slate-800 whitespace-nowrap">
                        {item.material_code}
                      </td>
                      <td
                        className="px-2 py-2 font-medium text-slate-900 break-words leading-snug min-w-[180px] sm:min-w-[220px]"
                        title={item.material_name}
                      >
                        {item.material_name}
                      </td>
                      <td className="px-2 py-2 text-center text-xs text-slate-600 whitespace-nowrap">{item.uom}</td>
                      <td className="px-2 py-2 text-right font-bold tabular-nums text-blue-700 whitespace-nowrap">
                        {formatVnNumber(item.outbound_qty)}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-xs text-slate-600 whitespace-nowrap">
                        {item.issue_count} lần
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Table 2: Top 10 Stock Items */}
        <div className="flex flex-col min-w-0 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white p-3 sm:p-4 shadow-[var(--shadow-level-1)]">
          <div className="mb-3 min-h-[50px] flex flex-col justify-start">
            <h3 className="text-base font-semibold text-slate-900 leading-snug">
              Top 10 vật tư có tồn kho lớn nhất
            </h3>
            <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
              Xếp hạng theo tổng sản lượng vật tư hiện có tại các kho
            </p>
          </div>

          <div className="overflow-x-auto rounded border border-[var(--color-border)]">
            <table className="w-full min-w-[540px] 2xl:min-w-0 2xl:table-fixed divide-y divide-[var(--color-border)] text-left text-xs sm:text-sm">
              <thead className="bg-slate-50 text-[11px] sm:text-xs font-semibold uppercase tracking-tight text-[var(--color-text-muted)]">
                <tr>
                  <th scope="col" className="px-1.5 py-2 text-center w-7 whitespace-nowrap">
                    STT
                  </th>
                  <th scope="col" className="px-1.5 py-2 whitespace-nowrap w-24">
                    Mã VT
                  </th>
                  <th scope="col" className="px-1.5 py-2 min-w-[170px] sm:min-w-[220px]">
                    Tên vật tư
                  </th>
                  <th scope="col" className="px-1 py-2 text-center w-9 whitespace-nowrap">
                    ĐVT
                  </th>
                  <th scope="col" className="px-1.5 py-2 text-right w-18 leading-tight">
                    Tồn thực tế
                  </th>
                  <th scope="col" className="px-1.5 py-2 text-right w-16 leading-tight">
                    Khả dụng
                  </th>
                  <th scope="col" className="px-1.5 py-2 text-right w-24 leading-tight">
                    Giá trị ước tính
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)] bg-white">
                {data.topStockItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="sticky left-0 px-3 py-6 text-center text-xs text-[var(--color-text-muted)] italic">
                      Không có dữ liệu tồn kho
                    </td>
                  </tr>
                ) : (
                  data.topStockItems.map((item, idx) => (
                    <tr key={item.id} className="hover:bg-slate-50/80">
                      <td className="px-1.5 py-2 text-center text-xs text-[var(--color-text-muted)] whitespace-nowrap">
                        {idx + 1}
                      </td>
                      <td className="px-1.5 py-2 font-mono text-xs font-semibold text-slate-800 whitespace-nowrap">
                        {item.material_code}
                      </td>
                      <td
                        className="px-1.5 py-2 font-medium text-slate-900 break-words leading-snug min-w-[170px] sm:min-w-[220px]"
                        title={item.material_name}
                      >
                        {item.material_name}
                      </td>
                      <td className="px-1 py-2 text-center text-xs text-slate-600 whitespace-nowrap">{item.uom}</td>
                      <td className="px-1.5 py-2 text-right font-bold tabular-nums text-slate-900 whitespace-nowrap">
                        {formatVnNumber(item.physical_stock)}
                      </td>
                      <td className="px-1.5 py-2 text-right font-medium tabular-nums text-emerald-700 whitespace-nowrap">
                        {formatVnNumber(item.available_stock)}
                      </td>
                      <td className="px-1.5 py-2 text-right tabular-nums text-xs text-slate-700 whitespace-nowrap">
                        {item.total_value != null && item.total_value > 0 ? (
                          `${formatVnNumber(item.total_value)} ₫`
                        ) : (
                          <span className="text-slate-400 italic text-[11px]">Chưa có giá</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-[var(--color-text-muted)] italic">
            * 'Chưa có giá': Vật tư chưa phát sinh đơn giá mua trong các đơn mua hàng (PO) đã duyệt.
          </p>
        </div>
      </div>    </div>
  )
}
